import React, { useContext, useState, useEffect, useRef, useCallback, memo } from "react";
import { LoggerContext } from "context/LoggerContext";
import styles from "./CardsListSection.module.scss";
import SectionWrapper from "components/elements/SectionWrapper";
import CardHorizontal from "components/elements/Card/CardHorizontal";
import Loading from "components/Loading";
import CardsListSentinel from "./CardsListSentinel";

/**
 * Card item renderer as memoized component to prevent re-rendering
 * of existing cards when new ones are added
 */
const CardItem = memo(({ item, index, forceLoad }) => {
	return (
		<div className="col-12 mb-4">
			<CardHorizontal
				title={item.title} 
				short_desc={item.short_desc}
				desc={item.desc} 
				cta_text={item.cta_text} 
				cta_link={item.cta_link} 
				image={(item.media || [])[0]?.mediaUrl || (item.medias || [])[0]?.mediaUrl}
				image_alt={(item.media || [])[0]?.alt_text || (item.medias || [])[0]?.alt_text || item.title} 
				bg_image={item.bg_image}
				bg_image_alt={item.bg_image_alt}
				svg={item.svg}
				footer={item.footer}
				forceLoad={forceLoad}
			/>
		</div>
	);
}, (prevProps, nextProps) => {
	// Deep comparison to prevent unnecessary re-renders
	return prevProps.item.id === nextProps.item.id && 
		prevProps.forceLoad === nextProps.forceLoad;
});

CardItem.displayName = "CardItem";

/**
 * Section component that displays cards in a list format with infinite scroll
 * Directly manages loading and DOM preservation for stable scrolling
 */
const CardsListSection = (props) => {
	const { logDebug } = useContext(LoggerContext);
	
	// Extract needed props with default values
	const {
		data = [],
		loading: externalLoading = false,
		error: externalError = null,
		pagination,
		items_per_page = 10
	} = props;
	
	// States for rendering and UI
	const [displayedItems, setDisplayedItems] = useState([]);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMoreItems, setHasMoreItems] = useState(true);
	const [paginationInitialized, setPaginationInitialized] = useState(false);
	
	// Initial rendering phase tracking
	const [isFirstRender, setIsFirstRender] = useState(true);
	const [emptyResponsesCount, setEmptyResponsesCount] = useState(0);
	// New state to track whether initial data has been loaded
	const [initialDataLoaded, setInitialDataLoaded] = useState(false);
	
	// Refs for DOM and infinite scroll
	const cardsContainerRef = useRef(null);
	const scrollYRef = useRef(0);
	const triggerTimeoutRef = useRef(null);
	const isLoadingRef = useRef(false); 
	const lastPageLoadedRef = useRef(pagination?.currentPage || 1);
	const loadLockRef = useRef(false);
	const loadLockTimeoutRef = useRef(null);
	const lastTriggerTimeRef = useRef(0);
	const processedItemsRef = useRef(new Set());
	const initialRenderTimerRef = useRef(null);
	const initialDataProcessedRef = useRef(false);
	
	// Track first data load
	useEffect(() => {
		if (isFirstRender && data && data.length > 0 && !externalLoading) {
			initialRenderTimerRef.current = setTimeout(() => {
				setIsFirstRender(false);
				logDebug("Initial render phase completed");
				
				// Mark that initial data has been loaded after a delay
				// to ensure DOM has been updated
				setTimeout(() => {
					setInitialDataLoaded(true);
					initialDataProcessedRef.current = true;
					logDebug("Initial data fully processed, sentinel can now be activated");
				}, 500);
			}, 500);
		}
		
		return () => {
			if (initialRenderTimerRef.current) {
				clearTimeout(initialRenderTimerRef.current);
			}
		};
	}, [data, externalLoading, isFirstRender, logDebug]);
	
	// Process incoming data and add new items
	useEffect(() => {
		if (!data || !Array.isArray(data)) return;
		
		// Find new items not already in displayed items
		const newItems = data.filter(item => {
			const itemId = item.id || item.cta_link || item.title;
			return !processedItemsRef.current.has(itemId);
		});
		
		// Mark items as processed
		newItems.forEach(item => {
			const itemId = item.id || item.cta_link || item.title;
			processedItemsRef.current.add(itemId);
		});
		
		// Track empty responses to detect end of data
		if (data.length === 0) {
			setEmptyResponsesCount(prev => prev + 1);
		} else {
			setEmptyResponsesCount(0);
		}
		
		// If we've received two consecutive empty responses, we're likely at the end
		if (emptyResponsesCount >= 1) {
			setHasMoreItems(false);
		}
		
		// Add new items to displayed items
		if (newItems.length > 0) {
			setDisplayedItems(prev => [...prev, ...newItems]);
			
			// Record the last page that was successfully loaded
			if (pagination?.currentPage) {
				lastPageLoadedRef.current = pagination.currentPage;
			}
			
				// Mark that we have processed initial data (if this is the first batch)
			if (!initialDataProcessedRef.current && newItems.length > 0) {
				initialDataProcessedRef.current = true;
				
				// Use a small delay to ensure DOM has been updated
				setTimeout(() => {
					setInitialDataLoaded(true);
					logDebug("Initial data marked as loaded after processing first batch");
				}, 200);
			}
			
			// WICHTIG: Erst nach einer kurzen Verzögerung den Ladezustand zurücksetzen
			// damit die Komponenten Zeit haben, sich zu aktualisieren
			const resetTimeout = setTimeout(() => {
				// Restore scroll position if loading more
				if (loadingMore) {
					window.scrollTo(0, scrollYRef.current);
				}
				
				// Wichtig: Erst hier die Ladezustände zurücksetzen
				setLoadingMore(false);
				isLoadingRef.current = false;
				
				// Ensure lock is removed after data is loaded
				if (loadLockTimeoutRef.current) {
					clearTimeout(loadLockTimeoutRef.current);
				}
				loadLockRef.current = false;
				
				logDebug("Loading states reset after data processed", {
					newItems: newItems.length,
					totalItems: displayedItems.length + newItems.length,
					initialDataLoaded: initialDataLoaded
				});
			}, 150); // Kleine Verzögerung für DOM-Updates
		
			return () => clearTimeout(resetTimeout);
		} else {
			// Auch hier mit Verzögerung zurücksetzen
			setTimeout(() => {
				setLoadingMore(false);
				isLoadingRef.current = false;
				loadLockRef.current = false;
				
				// If we've received empty data but this is our first load,
				// still mark initial data as loaded
				if (!initialDataProcessedRef.current) {
					initialDataProcessedRef.current = true;
					setInitialDataLoaded(true);
					logDebug("Initial data marked as loaded (empty data set)");
				}
			}, 150);
		}
		
		logDebug("CardsListSection data processed", {
			totalItems: displayedItems.length + newItems.length,
			newItems: newItems.length,
			loadingMore: loadingMore,
			isLoading: isLoadingRef.current,
			loadLock: loadLockRef.current,
			isFirstRender,
			emptyResponsesCount,
			initialDataLoaded: initialDataLoaded,
			initialDataProcessedRef: initialDataProcessedRef.current
		});
	}, [data, loadingMore, pagination, logDebug, isFirstRender, emptyResponsesCount, initialDataLoaded, displayedItems.length]);
	
	// Update pagination status with improved reliability
	useEffect(() => {
		// Log debugging info
		logDebug("Pagination status check", {
			pagination,
			externalLoading,
			displayedItemsCount: displayedItems.length,
			isFirstRender,
			paginationInitialized,
			itemsPerPage: items_per_page,
			initialDataLoaded: initialDataLoaded
		});

		// During initial render or loading, always assume more items could exist
		if (isFirstRender || externalLoading) {
			setHasMoreItems(true);
			return;
		}

		// IMPORTANT: Enhanced logic for determining if more items exist
		if (!pagination) {
			// If we have items but no pagination info, assume more items could exist
			// UNLESS we've already received less items than expected per page
			const expectedItemsPerPage = items_per_page || 10;
			
			if (data && data.length > 0 && data.length < expectedItemsPerPage) {
				// We received fewer items than expected - likely the final page
				logDebug("Received fewer items than expected per page, likely at the end", {
					received: data.length,
					expected: expectedItemsPerPage
				});
				setHasMoreItems(false);
			} else if (displayedItems.length > 0) {
				setHasMoreItems(true);
			} else if (displayedItems.length === 0 && !externalLoading && !isFirstRender) {
				setHasMoreItems(false);
			} else {
				setHasMoreItems(true);
			}
			return;
		}
		
		// When pagination info is available
		const currentPage = pagination.currentPage || 1;
		const totalPages = pagination.totalPages || 1;
		
		// IMPROVED LOGIC: Don't just check page numbers, also check data characteristics
		let hasMore = currentPage < totalPages;
		
		// If API says no more pages but we're getting normal data flow, be skeptical
		if (!hasMore && data && data.length >= (items_per_page || 10)) {
			logDebug("API reports no more pages but full data set received - assuming more data exists");
			hasMore = true;
		}
		
		// If we've received less data than expected per page, likely at the end
		if (hasMore && data && data.length > 0 && data.length < (items_per_page || 10)) {
			logDebug("Partial page received, likely at end of data");
			hasMore = false;
		}
		
		logDebug("Pagination evaluation completed", {
			currentPage,
			totalPages,
			hasMore,
			previousHasMore: hasMoreItems,
			isFirstRender,
			dataLength: data?.length,
			expectedPerPage: items_per_page || 10,
			initialDataLoaded: initialDataLoaded
		});
		
		setHasMoreItems(hasMore);
		setPaginationInitialized(true);
	}, [pagination, displayedItems.length, externalLoading, logDebug, hasMoreItems, isFirstRender, data, items_per_page, initialDataLoaded, paginationInitialized]);
	
	// Handle loading more items
	const handleLoadMore = useCallback(() => {
		// Prevent multiple triggers
		if (isLoadingRef.current || loadLockRef.current || !pagination?.setCurrentPage || loadingMore || !hasMoreItems) return;
		
		// Save scroll position
		scrollYRef.current = window.scrollY;
		
		// Set loading states
		setLoadingMore(true);
		isLoadingRef.current = true;
		loadLockRef.current = true;
		
		// Set auto-unlock timer
		if (loadLockTimeoutRef.current) {
			clearTimeout(loadLockTimeoutRef.current);
		}
		loadLockTimeoutRef.current = setTimeout(() => {
			loadLockRef.current = false;
		}, 5000);
		
		// Update last trigger time
		lastTriggerTimeRef.current = Date.now();
		
		logDebug("Load more triggered", {
			currentPage: pagination?.currentPage || 1,
			scrollY: scrollYRef.current,
			isFirstRender,
			initialDataLoaded: initialDataLoaded
		});
		
		// Load next page with slight delay to allow UI to update
		setTimeout(() => {
			pagination.setCurrentPage(pagination.currentPage + 1);
		}, 10);
	}, [pagination, loadingMore, hasMoreItems, logDebug, isFirstRender, initialDataLoaded]);
	
	// Cleanup timeouts on unmount
	useEffect(() => {
		// Capture current timeout values inside the effect
		const triggerTimeout = triggerTimeoutRef.current;
		const loadLockTimeout = loadLockTimeoutRef.current;
		const initialRenderTimer = initialRenderTimerRef.current;
		
		return () => {
			if (triggerTimeout) {
				clearTimeout(triggerTimeout);
			}
			
			if (loadLockTimeout) {
				clearTimeout(loadLockTimeout);
			}
			
			if (initialRenderTimer) {
				clearTimeout(initialRenderTimer);
			}
		};
	}, []);
	

	
	// Determine whether to show the end message - more conservative approach
	const showEndMessage = !hasMoreItems && !externalLoading && !isFirstRender && 
		displayedItems.length > 0 && emptyResponsesCount > 0;
	
	return (
		<SectionWrapper 
			{...props}
			className={`${styles.CardsListSection} ${loadingMore ? styles.loadingMore : ''} ${isFirstRender ? styles.initialRender : ''}`}
			fluid={false}
			data-initial-loaded={initialDataLoaded ? "true" : "false"}>
			
			{/* Error state */}
			{externalError && (
				<div className="alert alert-danger" role="alert">
					Fehler beim Laden der Daten: {externalError}
				</div>
			)}
			
			{/* Loading state for initial load */}
			{externalLoading && displayedItems.length === 0 ? (
				<div className="text-center py-4">
					<Loading />
				</div>
			) : (
				<div className="container p-0" ref={cardsContainerRef}>
					{/* Card items */}
					{displayedItems.length > 0 ? (
						<div className="row">
							{displayedItems.map((item, index) => (
								<CardItem
									key={item.id || item.cta_link || `item-${index}`}
									item={item}
									index={index}
									forceLoad={false}
								/>
							))}
						</div>
					) : !externalLoading && (
						<div className="text-center py-5">
							<p>Keine Einträge gefunden.</p>
						</div>
					)}
					
					{/* Verwende CardsListSentinel für Infinite Scroll */}
					<CardsListSentinel
						onIntersect={handleLoadMore}
						hasMorePages={hasMoreItems || externalLoading || isFirstRender}
						isLoading={externalLoading || isLoadingRef.current}
						isLoadingMore={loadingMore} 
						initialDataLoaded={initialDataLoaded} // Pass the new control prop
						loadMoreText="Weitere Einträge werden geladen..."
						endMessage="Sie haben das Ende der Liste erreicht."
						showEndMessage={showEndMessage}
					/>
				</div>
			)}
		</SectionWrapper>
	);
};

export default memo(CardsListSection);